import { Fragment, useState } from 'react';
import StatusBadge from './StatusBadge';
import PaymentStatusBadge from './PaymentStatusBadge';
import OrderDetailsPanel from './OrderDetailsPanel';
import { formatPKR } from '../api';
import { formatOrderDate, formatOrderTime } from '../utils/orderUtils';
import { formatPaymentMethodDisplay } from '../utils/paymentUtils';

function PaymentMethodCell({ order }) {
  return (
    <span className="font-medium text-lumora-dark-wood">
      {formatPaymentMethodDisplay(order)}
    </span>
  );
}

export default function OrderMonthGroups({
  groups,
  showActions,
  onAdvance,
  nextStatus,
  onCancel,
  renderExtra,
  canCancelOrder,   // optional fn(order) → bool; defaults to status === 'Pending'
}) {
  const [expandedId, setExpandedId] = useState(null);

  if (groups.length === 0) {
    return (
      <div className="glass-card text-center text-lumora-text/50 py-12">No orders found</div>
    );
  }

  // +2 for the two new payment columns
  const colCount = 9 + (showActions ? 1 : 0) + (renderExtra ? 1 : 0);

  const toggleDetails = (orderId) => {
    setExpandedId((prev) => (prev === orderId ? null : orderId));
  };

  return (
    <div className="space-y-8">
      {groups.map((group) => (
        <section key={group.key}>
          <h2 className="font-serif text-xl text-lumora-dark-wood mb-4 pb-2 border-b border-lumora-secondary/80">
            {group.label}
          </h2>
          <div className="table-wrap">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Order ID</th>
                  <th>Date</th>
                  <th>Time</th>
                  <th>Products</th>
                  <th>Customer</th>
                  <th>Total</th>
                  <th>Payment</th>
                  <th>Payment Status</th>
                  <th>Status</th>
                  {showActions && <th>Action</th>}
                  {renderExtra && <th>Details</th>}
                </tr>
              </thead>
              <tbody>
                {groups.flatMap((g) => g.orders).length === 0
                  ? null
                  : group.orders.map((order) => {
                      const expanded = expandedId === order.orderId;
                      const next = nextStatus?.(order.status);

                      return (
                        <Fragment key={order.orderId}>
                          <tr className={expanded ? 'bg-lumora-secondary/20' : undefined}>
                            {/* Order ID — expand toggle */}
                            <td className="font-mono">
                              <button
                                type="button"
                                onClick={() => toggleDetails(order.orderId)}
                                className="inline-flex items-center gap-2 text-left hover:text-lumora-wood transition-colors"
                                aria-expanded={expanded}
                              >
                                <span
                                  className={`inline-block text-[10px] transition-transform ${expanded ? 'rotate-90' : ''}`}
                                  aria-hidden
                                >
                                  ▶
                                </span>
                                #{order.orderId}
                              </button>
                            </td>

                            <td>{formatOrderDate(order.date)}</td>
                            <td>{formatOrderTime(order.time)}</td>

                            {/* Products */}
                            <td>
                              {order.items.map((item, i) => (
                                <div key={i} className="text-sm">
                                  {item.productName} × {item.quantity}
                                </div>
                              ))}
                            </td>

                            {/* Customer */}
                            <td>
                              <div>{order.customerName}</div>
                              <div className="text-xs text-lumora-text/50">{order.customerPhone}</div>
                            </td>

                            <td>{formatPKR(order.orderTotal)}</td>

                            {/* NEW: Payment Method */}
                            <td>
                              <PaymentMethodCell order={order} />
                            </td>

                            {/* NEW: Payment Status */}
                            <td>
                              <PaymentStatusBadge status={order.paymentStatus} />
                            </td>

                            {/* Order Status */}
                            <td>
                              <StatusBadge status={order.status} />
                            </td>

                            {/* Actions — horizontal layout, compact */}
                            {showActions && (
                              <td>
                                <div className="flex flex-row gap-1.5 items-center">
                                  {next &&
                                    onAdvance &&
                                    order.status !== 'Returned' &&
                                    order.status !== 'Cancelled' &&
                                    order.status !== 'Payment Failed' && (
                                      <button
                                        type="button"
                                        className="btn-primary text-xs whitespace-nowrap px-2.5 py-1"
                                        onClick={() => onAdvance(order.orderId, next)}
                                      >
                                        {next === 'Confirmed'
                                          ? 'Confirm'
                                          : next === 'Dispatched'
                                            ? 'Dispatch'
                                            : `→ ${next}`}
                                      </button>
                                    )}
                                  {onCancel &&
                                    (canCancelOrder ? canCancelOrder(order) : order.status === 'Pending') && (
                                      <button
                                        type="button"
                                        className="btn-secondary text-xs whitespace-nowrap px-2.5 py-1 text-red-700 border-red-200 hover:border-red-300"
                                        onClick={() => onCancel(order.orderId)}
                                      >
                                        Cancel
                                      </button>
                                    )}
                                </div>
                              </td>
                            )}

                            {renderExtra && <td>{renderExtra(order)}</td>}
                          </tr>

                          {/* Expanded details row */}
                          {expanded && (
                            <tr>
                              <td colSpan={colCount} className="!py-4 !px-4">
                                <OrderDetailsPanel order={order} />
                              </td>
                            </tr>
                          )}
                        </Fragment>
                      );
                    })}
              </tbody>
            </table>
          </div>
        </section>
      ))}
    </div>
  );
}
